import { getApiUrl } from "../config/apiEndpoints";
import { fetchWithAuth } from "../utils/tokenRefresh";
import {
  Team,
  Player,
  Pick,
  Transaction,
  Dashboard,
  DraftState,
  TransactionType,
} from "../objects/types";

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers as Record<string, string>),
  };

  const response = await fetchWithAuth(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Request failed: ${response.statusText}`);
  }

  return response.json();
}

// Teams
export async function listTeams(): Promise<Team[]> {
  const result = await fetchJson<{ teams: Team[] }>(getApiUrl("/api/teams"));
  return result.teams;
}

export async function getTeam(uuid: string): Promise<Team> {
  const result = await fetchJson<{ team: Team }>(
    getApiUrl(`/api/teams/${uuid}`)
  );
  return result.team;
}

export async function createTeam(name: string): Promise<Team> {
  const result = await fetchJson<{ team: Team }>(getApiUrl("/api/teams"), {
    method: "POST",
    body: JSON.stringify({ name }),
  });
  return result.team;
}

export async function updateTeam(uuid: string, name: string): Promise<Team> {
  const result = await fetchJson<{ team: Team }>(
    getApiUrl(`/api/teams/${uuid}`),
    {
      method: "PUT",
      body: JSON.stringify({ name }),
    }
  );
  return result.team;
}

export async function deleteTeam(uuid: string): Promise<void> {
  await fetchJson(getApiUrl(`/api/teams/${uuid}`), { method: "DELETE" });
}

// Players
export async function listPlayers(): Promise<Player[]> {
  const result = await fetchJson<{ players: Player[] }>(
    getApiUrl("/api/players")
  );
  return result.players;
}

export async function getPlayer(uuid: string): Promise<Player> {
  const result = await fetchJson<{ player: Player }>(
    getApiUrl(`/api/players/${uuid}`)
  );
  return result.player;
}

export async function createPlayer(name: string): Promise<Player> {
  const result = await fetchJson<{ player: Player }>(getApiUrl("/api/players"), {
    method: "POST",
    body: JSON.stringify({ name }),
  });
  return result.player;
}

export async function updatePlayer(
  uuid: string,
  name: string
): Promise<Player> {
  const result = await fetchJson<{ player: Player }>(
    getApiUrl(`/api/players/${uuid}`),
    {
      method: "PUT",
      body: JSON.stringify({ name }),
    }
  );
  return result.player;
}

export async function deletePlayer(uuid: string): Promise<void> {
  await fetchJson(getApiUrl(`/api/players/${uuid}`), { method: "DELETE" });
}

// Picks
export async function listPicks(year?: number): Promise<Pick[]> {
  const url = year
    ? getApiUrl(`/api/picks?year=${year}`)
    : getApiUrl("/api/picks");
  const result = await fetchJson<{ picks: Pick[] }>(url);
  return result.picks;
}

export async function getPick(uuid: string): Promise<Pick> {
  const result = await fetchJson<{ pick: Pick }>(
    getApiUrl(`/api/picks/${uuid}`)
  );
  return result.pick;
}

export async function createPick(data: {
  natural_team_uuid: string;
  round: number;
  year: number;
  is_kp_pick?: boolean;
  pick_number?: number;
  actual_team_uuid?: string;
  pick_result_player_uuid?: string;
}): Promise<Pick> {
  const result = await fetchJson<{ pick: Pick }>(getApiUrl("/api/picks"), {
    method: "POST",
    body: JSON.stringify(data),
  });
  return result.pick;
}

export async function updatePick(
  uuid: string,
  updates: Partial<Pick>
): Promise<Pick> {
  const result = await fetchJson<{ pick: Pick }>(
    getApiUrl(`/api/picks/${uuid}`),
    {
      method: "PUT",
      body: JSON.stringify(updates),
    }
  );
  return result.pick;
}

export async function deletePick(uuid: string): Promise<void> {
  await fetchJson(getApiUrl(`/api/picks/${uuid}`), { method: "DELETE" });
}

// Transactions
export async function listTransactions(year?: number): Promise<Transaction[]> {
  const url = year
    ? getApiUrl(`/api/transactions?year=${year}`)
    : getApiUrl("/api/transactions");
  const result = await fetchJson<{ transactions: Transaction[] }>(url);
  return result.transactions;
}

export async function getTransaction(uuid: string): Promise<Transaction> {
  const result = await fetchJson<{ transaction: Transaction }>(
    getApiUrl(`/api/transactions/${uuid}`)
  );
  return result.transaction;
}

export async function createTransaction(data: {
  transaction_type: TransactionType;
  year: number;
  team_uuid?: string;
  player_uuid?: string;
  kp_amount?: number;
  details?: Record<string, unknown>;
}): Promise<Transaction> {
  const result = await fetchJson<{ transaction: Transaction }>(
    getApiUrl("/api/transactions"),
    {
      method: "POST",
      body: JSON.stringify(data),
    }
  );
  return result.transaction;
}

export async function deleteTransaction(uuid: string): Promise<void> {
  await fetchJson(getApiUrl(`/api/transactions/${uuid}`), { method: "DELETE" });
}

// Dashboard
export async function getDashboard(year: number): Promise<Dashboard> {
  const result = await fetchJson<Dashboard>(
    getApiUrl(`/api/dashboard?year=${year}`)
  );
  return result;
}

// Draft state
export async function getDraftState(year: number): Promise<DraftState> {
  const result = await fetchJson<DraftState>(getApiUrl(`/api/draft/${year}`));
  return result;
}
